/**
 * Helpers for "chunk load" failures right after a deployment: the old
 * lazy-loaded chunks no longer exist on the server, so navigating to a lazy
 * route throws "Loading chunk X failed" / "Failed to fetch dynamically
 * imported module".
 *
 * On ADX these surface through the Router's navigation error handler (the app
 * is zoneless and rejects the navigation promise), not through Angular's
 * global ErrorHandler — so both wire in the same logic via these helpers.
 */

// sessionStorage key for the timestamp of the last chunk-error reload. Guards
// against infinite reload loops when a chunk keeps failing for a reason other
// than a stale deployment.
const CHUNK_RELOAD_KEY = 'chunk_load_reloaded_at';

// sessionStorage key for the URL the user was on before the chunk-error
// reload. The chunk fails before the navigation commits, so this is the page
// being left (one that loads fine). If the reload to the target still fails,
// we send the user back here instead of stranding them on the broken route.
const CHUNK_PREVIOUS_URL_KEY = 'chunk_load_previous_url';

// If another chunk error arrives within this window after a reload, assume the
// reload did not fix it and fall back to the normal error flow. Older reloads
// are treated as unrelated (e.g. a later deployment), so the guard expires.
const CHUNK_RELOAD_WINDOW_MS = 15_000;

// Prevent multiple error handlers from triggering the same reload during a
// single page load. The sessionStorage guard handles reloads; this one handles
// duplicate events in memory.
let handlingChunkError = false;

/**
 * Detects the "chunk load" failure thrown when a lazy-loaded route's chunk is
 * no longer present on the server (typically right after a deployment).
 */
export function isChunkLoadError(error: unknown): boolean {
  const candidate = (error as any)?.rejection ?? error;
  const name = candidate?.name ?? '';
  const message = candidate?.message ?? '';

  return (
    name === 'ChunkLoadError' ||
    // webpack
    /Loading chunk [\w-]+ failed/i.test(message) ||
    // esbuild / native dynamic import (Chrome, Firefox)
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    // es-module-shims (native-federation): "<status> <statusText> <url>",
    // e.g. "404 Not Found https://.../chunk-B4WZX2OU.js"
    /\b[45]\d\d\b.*\.m?js(\?|#|$|\s)/i.test(message) ||
    // es-module-shims (native-federation): chunk fetch rejected before an HTTP
    // response was available (e.g. stale cross-origin/CDN chunk).
    /Unable to fetch .*\.m?js\b/i.test(message)
  );
}

/**
 * Silently reloads the app to fetch the latest deployed version. Guards
 * against an infinite reload loop when the chunk keeps failing for a reason
 * unrelated to a stale deployment: if it still fails right after a reload, the
 * user is sent back to the page they were on (which loads fine) instead of
 * left on the broken route. Returns true if a reload or back-navigation ran.
 *
 * @param targetUrl The URL the user was navigating to when the chunk failed
 *   (e.g. `NavigationError.url`). The chunk fails before navigation commits,
 *   so the browser is still on the previous URL — reloading would strand the
 *   user there.
 */
export function reloadForChunkError(targetUrl?: string): boolean {
  // A reload/back-navigation is already in flight for this page load; dedupe so
  // concurrent handlers for the same failure don't trigger a second navigation.
  if (handlingChunkError) {
    return false;
  }

  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
  const elapsed = Date.now() - lastReloadAt;

  if (lastReloadAt && elapsed >= 0 && elapsed < CHUNK_RELOAD_WINDOW_MS) {
    // Chunk failed before navigation committed; no previous URL. Fallback to home.
    const previousUrl = sessionStorage.getItem(CHUNK_PREVIOUS_URL_KEY) ?? '/';

    sessionStorage.removeItem(CHUNK_PREVIOUS_URL_KEY);

    handlingChunkError = true;
    console.error(
      '[ChunkLoadError] Reload did not resolve the chunk failure; returning to the previous page.',
    );
    location.assign(previousUrl);
    return true;
  }

  handlingChunkError = true;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  console.warn('[ChunkLoadError] Reloading to fetch the latest version.');

  if (targetUrl) {
    if (new URL(targetUrl, location.origin).href !== location.href) {
      sessionStorage.setItem(CHUNK_PREVIOUS_URL_KEY, location.href);
    } else {
      sessionStorage.removeItem(CHUNK_PREVIOUS_URL_KEY);
    }

    location.assign(targetUrl);
  } else {
    sessionStorage.removeItem(CHUNK_PREVIOUS_URL_KEY);
    location.reload();
  }
  return true;
}
